"use client";

import { c as _c } from "react/compiler-runtime";
import { useBreakpoint } from "@dc/hooks";
import { cn } from "@dc/ui";
import { Avatar, Button, Divider, EllipsisTooltip, Input, Listbox, ListboxItem, Popover, PopoverContent, PopoverTrigger, ScrollShadow, Spinner, Tooltip } from "@dc/ui/components";
import type { Selection } from "@heroui/react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import type { FC, ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AltArrowDownLinear, ClockCircleLinear, Logout3Linear, MagniferLinear, QuestionCircleLinear, UserLinear } from "solar-icon-set";
import { Can } from "@/components/Ability/Can";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useMe } from "@/hooks/useMe";
import { useNavigationUpdatesEnabled } from "@/hooks/useNavigationUpdatesEnabled";
import { usePylon } from "@/hooks/usePylon";
import { getAvatarSrc } from "@/lib/avatarSrc";
import { useOrgTeamPath } from "@/lib/orgTeamPath";
import { ELEMENT_TYPE, trackElement } from "@/lib/tracking/analytics";
import { InternalToolsSection } from "./components/InternalToolsSection";
import { MenuRow } from "./components/MenuRow";
import { MenuSectionLabel } from "./components/MenuSectionLabel";
import { UserSettingsSection } from "./components/UserSettingsSection";
import { useOrgSwitcher } from "./hooks/useOrgSwitcher/useOrgSwitcher";
export interface OrgSwitcherProps {
  trigger?: ReactNode;
  isCollapsed?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  /** Organization id -> disabled reason. Matching orgs render disabled with the reason shown inline. Overrides the switcher's own entitlement-derived map when passed. */
  disabledOrganizations?: Record<string, string>;
  /** Called with the new org's id right after a selection updates context. The everyday sidebar usage doesn't need this — a permanently-mounted TeamSwitcher reacts to the context change itself and navigates. Callers that render OrgSwitcher outside that tree (no TeamSwitcher to react) must handle navigation themselves via this callback. */
  onOrganizationSelected?: (organizationId: string) => void;
}
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SCROLL_THRESHOLD = 100;
export const OrgSwitcher: FC<OrgSwitcherProps> = t0 => {
  const $ = _c(186);
  const {
    trigger,
    isCollapsed: t1,
    onOpenChange,
    disabledOrganizations: disabledOrganizationsProp,
    onOrganizationSelected
  } = t0;
  const isCollapsed = t1 === undefined ? false : t1;
  const [isOpen, setIsOpen] = useState(false);
  const scrollRef = useRef(null);
  const {
    me,
    userId: myUserId
  } = useMe();
  const isDesktop = useBreakpoint("md") ?? true;
  const t = useTranslations("navigation.org_switcher");
  const tActions = useTranslations("navigation.actions");
  const navigationUpdatesEnabled = useNavigationUpdatesEnabled();
  const {
    toggleChat
  } = usePylon();
  const router = useRouter();
  const {
    build
  } = useOrgTeamPath();
  const {
    "pd-6-user-profile": userProfileEnabled,
    "trials-enable-provisioning-pd-6": trialsEnabled
  } = useFeatureFlags();
  const {
    organizations,
    currentOrganization,
    currentOrganizationId,
    totalCount,
    hasMore,
    isPending,
    isFetchingMore,
    showSearch,
    searchValue,
    setSearchValue,
    loadMore,
    selectOrganization,
    handleLogout,
    isLoggingOut,
    disabledOrganizations: fetchedDisabledOrganizations
  } = useOrgSwitcher();
  const disabledOrganizations = disabledOrganizationsProp ?? fetchedDisabledOrganizations;
  const currentOrgName = currentOrganization?.attributes?.name ?? null;
  const hasMultipleOrgs = totalCount > 1;
  let t2;
  if ($[0] !== onOpenChange) {
    t2 = open => {
      setIsOpen(open);
      onOpenChange?.(open);
    };
    $[0] = onOpenChange;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleOpenChange = t2;
  let t3;
  if ($[2] !== build || $[3] !== handleOpenChange || $[4] !== router) {
    t3 = logicalPath => {
      router.push(build(logicalPath));
      handleOpenChange(false);
    };
    $[2] = build;
    $[3] = handleOpenChange;
    $[4] = router;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const navigateTo = t3;
  let t4;
  let t5;
  if ($[6] !== isOpen) {
    t4 = () => {
      if (isOpen) {
        document.documentElement.dataset.overlayOpen = "true";
      } else {
        delete document.documentElement.dataset.overlayOpen;
      }
      return _temp;
    };
    t5 = [isOpen];
    $[6] = isOpen;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== hasMore || $[10] !== isFetchingMore || $[11] !== loadMore) {
    t6 = e => {
      const element = e.currentTarget;
      const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < SCROLL_THRESHOLD;
      if (isNearBottom && hasMore && !isFetchingMore) {
        loadMore();
      }
    };
    $[9] = hasMore;
    $[10] = isFetchingMore;
    $[11] = loadMore;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const handleScroll = t6;
  let t7;
  if ($[13] !== currentOrganizationId || $[14] !== handleOpenChange || $[15] !== onOrganizationSelected || $[16] !== organizations || $[17] !== selectOrganization) {
    t7 = keys => {
      if (keys === "all") {
        return;
      }
      const selectedKey = Array.from(keys)[0];
      if (selectedKey && String(selectedKey) !== currentOrganizationId) {
        const selectedOrg = organizations.find(org => org.id === String(selectedKey));
        if (selectedOrg) {
          selectOrganization(selectedOrg);
          onOrganizationSelected?.(selectedOrg.id);
          handleOpenChange(false);
        }
      }
    };
    $[13] = currentOrganizationId;
    $[14] = handleOpenChange;
    $[15] = onOrganizationSelected;
    $[16] = organizations;
    $[17] = selectOrganization;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleSelectionChange = t7;
  me?.data?.attributes?.firstName;
  me?.data?.attributes?.lastName;
  let t8;
  if ($[19] !== me?.data?.attributes?.firstName) {
    t8 = me?.data?.attributes?.firstName?.trim();
    $[19] = me?.data?.attributes?.firstName;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const firstName = t8;
  let t9;
  if ($[21] !== me?.data?.attributes?.lastName) {
    t9 = me?.data?.attributes?.lastName?.trim();
    $[21] = me?.data?.attributes?.lastName;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const lastName = t9;
  let t10;
  if ($[23] !== firstName || $[24] !== lastName) {
    t10 = [firstName, lastName].filter(Boolean);
    $[23] = firstName;
    $[24] = lastName;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  const nameParts = t10 as string[];
  const displayName = nameParts.join(" ") || "User";
  let t11;
  if ($[26] !== displayName) {
    t11 = UUID_REGEX.test(displayName);
    $[26] = displayName;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  const isUUID = t11;
  let t12;
  if ($[28] !== displayName || $[29] !== isUUID) {
    t12 = {
      displayName,
      isUUID
    };
    $[28] = displayName;
    $[29] = isUUID;
    $[30] = t12;
  } else {
    t12 = $[30];
  }
  const userData = t12;
  const t13 = !userData.isUUID ? userData.displayName : undefined;
  let t14;
  if ($[31] !== myUserId) {
    t14 = getAvatarSrc(myUserId);
    $[31] = myUserId;
    $[32] = t14;
  } else {
    t14 = $[32];
  }
  let t15;
  if ($[33] !== t13 || $[34] !== t14 || $[35] !== userData.displayName) {
    t15 = <Avatar name={t13} src={t14} className="size-6" aria-label={userData.displayName} />;
    $[33] = t13;
    $[34] = t14;
    $[35] = userData.displayName;
    $[36] = t15;
  } else {
    t15 = $[36];
  }
  const avatarElement = t15;
  const t16 = userData.displayName;
  let t17;
  if ($[37] !== t) {
    t17 = t("account_and_organization_menu");
    $[37] = t;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== currentOrgName || $[40] !== t17 || $[41] !== userData.displayName) {
    t18 = [t16, currentOrgName, t17].filter(Boolean);
    $[39] = currentOrgName;
    $[40] = t17;
    $[41] = userData.displayName;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const triggerLabel = t18.join(", ");
  const avatarOnly = isCollapsed || navigationUpdatesEnabled;
  const t19 = avatarOnly ? "justify-center" : "justify-start";
  let t20;
  if ($[43] !== t19) {
    t20 = cn("h-auto bg-transparent! py-2 text-zinc-300! transition-all hover:bg-navy-700! hover:text-zinc-300!", t19);
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  const t21 = !avatarOnly;
  let t22;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = trackElement(ELEMENT_TYPE.Button, "User Menu");
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  let t23;
  if ($[46] !== avatarElement || $[47] !== avatarOnly || $[48] !== currentOrgName || $[49] !== hasMultipleOrgs || $[50] !== userData.displayName) {
    t23 = avatarOnly ? avatarElement : <div className="flex w-full items-center gap-2"><div className="shrink-0">{avatarElement}</div><div className="flex min-w-0 flex-1 flex-col items-start"><EllipsisTooltip as="span" content={userData.displayName} className="w-full text-left text-sm">{userData.displayName}</EllipsisTooltip>{currentOrgName && <span data-testid="org-switcher-current-org" className="contents"><EllipsisTooltip as="span" content={currentOrgName} className="contents w-full text-left text-xs leading-0">{currentOrgName}</EllipsisTooltip></span>}</div>{hasMultipleOrgs && <AltArrowDownLinear className="size-4 shrink-0 text-default-400" />}</div>;
    $[46] = avatarElement;
    $[47] = avatarOnly;
    $[48] = currentOrgName;
    $[49] = hasMultipleOrgs;
    $[50] = userData.displayName;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== avatarOnly || $[53] !== t20 || $[54] !== t21 || $[55] !== t23 || $[56] !== triggerLabel) {
    t24 = <Button className={t20} isIconOnly={avatarOnly} fullWidth={t21} aria-label={triggerLabel} {...t22}>{t23}</Button>;
    $[52] = avatarOnly;
    $[53] = t20;
    $[54] = t21;
    $[55] = t23;
    $[56] = triggerLabel;
    $[57] = t24;
  } else {
    t24 = $[57];
  }
  const defaultTrigger = t24;
  const showOnlyLogout = !isPending && organizations.length <= 1 && !searchValue;
  let t25;
  if ($[58] !== currentOrganizationId || $[59] !== disabledOrganizations || $[60] !== handleSelectionChange || $[61] !== isPending || $[62] !== organizations || $[63] !== t) {
    t25 = isPending ? <div className="flex items-center justify-center py-8"><Spinner size="sm" /></div> : organizations.length === 0 ? <div className="py-8 text-center text-default-500 text-sm">{t("no_organizations_found")}</div> : <Listbox aria-label={t("organizations")} selectionMode="single" selectedKeys={currentOrganizationId ? new Set([currentOrganizationId]) : new Set()} disabledKeys={new Set(Object.keys(disabledOrganizations))} onSelectionChange={handleSelectionChange} classNames={{
      list: "gap-0"
    }}>{organizations.map(org_0 => {
        const orgName = org_0.attributes?.name ?? "Unnamed Organization";
        const disabledReason = disabledOrganizations[org_0.id];
        return <ListboxItem key={org_0.id} textValue={orgName} description={disabledReason} classNames={{
          title: "max-w-[180px]"
        }} {...trackElement(ELEMENT_TYPE.DropdownItem, "Switch Organization", {
          org_name: orgName
        })}><EllipsisTooltip content={orgName} className="max-w-[180px]" placement="right" color="foreground">{orgName}</EllipsisTooltip></ListboxItem>;
      })}</Listbox>;
    $[58] = currentOrganizationId;
    $[59] = disabledOrganizations;
    $[60] = handleSelectionChange;
    $[61] = isPending;
    $[62] = organizations;
    $[63] = t;
    $[64] = t25;
  } else {
    t25 = $[64];
  }
  let t26;
  if ($[65] !== isFetchingMore) {
    t26 = isFetchingMore && <div className="flex items-center justify-center py-2"><Spinner size="sm" /></div>;
    $[65] = isFetchingMore;
    $[66] = t26;
  } else {
    t26 = $[66];
  }
  let t27;
  if ($[67] !== t25 || $[68] !== t26) {
    t27 = <>{t25}{t26}</>;
    $[67] = t25;
    $[68] = t26;
    $[69] = t27;
  } else {
    t27 = $[69];
  }
  const organizationList = t27;
  let t28;
  if ($[70] !== handleScroll || $[71] !== organizationList || $[72] !== searchValue || $[73] !== setSearchValue || $[74] !== showOnlyLogout || $[75] !== showSearch || $[76] !== t) {
    t28 = !showOnlyLogout && <>{showSearch && <div className="p-2 py-3"><Input placeholder={t("search_organizations")} value={searchValue} onValueChange={setSearchValue} startContent={<MagniferLinear className="text-default-400" />} size="sm" classNames={{
          inputWrapper: "bg-default-100",
          input: "text-base sm:text-sm"
        }} aria-label={t("search_organizations")} /></div>}<ScrollShadow ref={scrollRef} className="max-h-64" onScroll={handleScroll}>{organizationList}</ScrollShadow></>;
    $[70] = handleScroll;
    $[71] = organizationList;
    $[72] = searchValue;
    $[73] = setSearchValue;
    $[74] = showOnlyLogout;
    $[75] = showSearch;
    $[76] = t;
    $[77] = t28;
  } else {
    t28 = $[77];
  }
  const t29 = !showOnlyLogout && "border-border-divider-neutral-usual border-t";
  let t30;
  if ($[78] !== t29) {
    t30 = cn("p-2", t29);
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== build || $[81] !== handleOpenChange || $[82] !== myUserId || $[83] !== router || $[84] !== t || $[85] !== userProfileEnabled) {
    t31 = userProfileEnabled && <Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={<UserLinear className="size-5" />} onPress={() => {
      const path = myUserId ? `/profile/${myUserId}` : "/profile";
      router.push(build(path));
      handleOpenChange(false);
    }} aria-label={t("my_profile")} {...trackElement(ELEMENT_TYPE.Button, "My Profile")}>{t("my_profile")}</Button>;
    $[80] = build;
    $[81] = handleOpenChange;
    $[82] = myUserId;
    $[83] = router;
    $[84] = t;
    $[85] = userProfileEnabled;
    $[86] = t31;
  } else {
    t31 = $[86];
  }
  let t32;
  if ($[87] !== build || $[88] !== handleOpenChange || $[89] !== router || $[90] !== t || $[91] !== trialsEnabled) {
    t32 = trialsEnabled && <Can action="read" resource="TRIAL"><Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={<ClockCircleLinear className="size-5" />} onPress={() => {
        router.push(build("/trials"));
        handleOpenChange(false);
      }} aria-label={t("trials")} {...trackElement(ELEMENT_TYPE.Button, "Trials")}>{t("trials")}</Button></Can>;
    $[87] = build;
    $[88] = handleOpenChange;
    $[89] = router;
    $[90] = t;
    $[91] = trialsEnabled;
    $[92] = t32;
  } else {
    t32 = $[92];
  }
  let t33;
  if ($[93] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <Logout3Linear className="size-5" />;
    $[93] = t33;
  } else {
    t33 = $[93];
  }
  let t34;
  if ($[94] !== t) {
    t34 = t("logout");
    $[94] = t;
    $[95] = t34;
  } else {
    t34 = $[95];
  }
  let t35;
  if ($[96] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = trackElement(ELEMENT_TYPE.Button, "Log Out");
    $[96] = t35;
  } else {
    t35 = $[96];
  }
  let t36;
  if ($[97] !== t) {
    t36 = t("logout");
    $[97] = t;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  let t37;
  if ($[99] !== handleLogout || $[100] !== isLoggingOut || $[101] !== t34 || $[102] !== t36) {
    t37 = <Button fullWidth={true} variant="light" color="default" className="justify-start" startContent={t33} onPress={handleLogout} isLoading={isLoggingOut} aria-label={t34} {...t35}>{t36}</Button>;
    $[99] = handleLogout;
    $[100] = isLoggingOut;
    $[101] = t34;
    $[102] = t36;
    $[103] = t37;
  } else {
    t37 = $[103];
  }
  let t38;
  if ($[104] !== t30 || $[105] !== t31 || $[106] !== t32 || $[107] !== t37) {
    t38 = <div className={t30}>{t31}{t32}{t37}</div>;
    $[104] = t30;
    $[105] = t31;
    $[106] = t32;
    $[107] = t37;
    $[108] = t38;
  } else {
    t38 = $[108];
  }
  let t39;
  if ($[109] !== t28 || $[110] !== t38) {
    t39 = <PopoverContent className="max-w-72 p-0"><div className="flex flex-col">{t28}{t38}</div></PopoverContent>;
    $[109] = t28;
    $[110] = t38;
    $[111] = t39;
  } else {
    t39 = $[111];
  }
  const legacyPopoverContent = t39;
  const t40 = !userData.isUUID ? userData.displayName : undefined;
  let t41;
  if ($[112] !== myUserId) {
    t41 = getAvatarSrc(myUserId);
    $[112] = myUserId;
    $[113] = t41;
  } else {
    t41 = $[113];
  }
  let t42;
  if ($[114] !== t40 || $[115] !== t41) {
    t42 = <Avatar name={t40} src={t41} className="size-10 shrink-0" />;
    $[114] = t40;
    $[115] = t41;
    $[116] = t42;
  } else {
    t42 = $[116];
  }
  let t43;
  if ($[117] !== userData.displayName) {
    t43 = <EllipsisTooltip as="span" content={userData.displayName} className="w-full text-left font-medium text-sm">{userData.displayName}</EllipsisTooltip>;
    $[117] = userData.displayName;
    $[118] = t43;
  } else {
    t43 = $[118];
  }
  let t44;
  if ($[119] !== currentOrgName) {
    t44 = currentOrgName && <span data-testid="org-switcher-current-org" className="contents"><EllipsisTooltip as="span" content={currentOrgName} className="w-full text-left text-text-interactive-neutral-faint text-xs">{currentOrgName}</EllipsisTooltip></span>;
    $[119] = currentOrgName;
    $[120] = t44;
  } else {
    t44 = $[120];
  }
  let t45;
  if ($[121] !== t43 || $[122] !== t44) {
    t45 = <div className="flex min-w-0 flex-col">{t43}{t44}</div>;
    $[121] = t43;
    $[122] = t44;
    $[123] = t45;
  } else {
    t45 = $[123];
  }
  let t46;
  if ($[124] !== t42 || $[125] !== t45) {
    t46 = <div className="flex w-full items-center gap-3 px-4 py-3">{t42}{t45}</div>;
    $[124] = t42;
    $[125] = t45;
    $[126] = t46;
  } else {
    t46 = $[126];
  }
  let t47;
  if ($[127] !== handleScroll || $[128] !== organizationList || $[129] !== searchValue || $[130] !== setSearchValue || $[131] !== showOnlyLogout || $[132] !== showSearch || $[133] !== t) {
    t47 = !showOnlyLogout && <><Divider /><div className="flex w-full flex-col gap-1 p-2"><MenuSectionLabel>{t("organization")}</MenuSectionLabel>{showSearch && <Input placeholder={t("search_organizations")} value={searchValue} onValueChange={setSearchValue} startContent={<MagniferLinear className="text-default-400" />} size="sm" className="px-2" classNames={{
          inputWrapper: "bg-default-100",
          input: "text-base sm:text-sm"
        }} aria-label={t("search_organizations")} />}<ScrollShadow ref={scrollRef} className="max-h-48" onScroll={handleScroll}>{organizationList}</ScrollShadow></div></>;
    $[127] = handleScroll;
    $[128] = organizationList;
    $[129] = searchValue;
    $[130] = setSearchValue;
    $[131] = showOnlyLogout;
    $[132] = showSearch;
    $[133] = t;
    $[134] = t47;
  } else {
    t47 = $[134];
  }
  let t48;
  if ($[135] === Symbol.for("react.memo_cache_sentinel")) {
    t48 = <Divider />;
    $[135] = t48;
  } else {
    t48 = $[135];
  }
  let t49;
  if ($[136] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <QuestionCircleLinear className="size-5" />;
    $[136] = t49;
  } else {
    t49 = $[136];
  }
  let t50;
  if ($[137] !== tActions) {
    t50 = tActions("help_and_resources");
    $[137] = tActions;
    $[138] = t50;
  } else {
    t50 = $[138];
  }
  let t51;
  if ($[139] !== handleOpenChange || $[140] !== toggleChat) {
    t51 = () => {
      toggleChat();
      handleOpenChange(false);
    };
    $[139] = handleOpenChange;
    $[140] = toggleChat;
    $[141] = t51;
  } else {
    t51 = $[141];
  }
  let t52;
  if ($[142] !== t50 || $[143] !== t51) {
    t52 = <div className="flex w-full flex-col p-2"><MenuRow icon={t49} label={t50} onPress={t51} testId="sidebar-help" trackingLabel="Help and Resources" /></div>;
    $[142] = t50;
    $[143] = t51;
    $[144] = t52;
  } else {
    t52 = $[144];
  }
  let t53;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = <Divider />;
    $[145] = t53;
  } else {
    t53 = $[145];
  }
  let t54;
  if ($[146] !== handleOpenChange) {
    t54 = () => handleOpenChange(false);
    $[146] = handleOpenChange;
    $[147] = t54;
  } else {
    t54 = $[147];
  }
  let t55;
  if ($[148] !== navigateTo || $[149] !== t54) {
    t55 = <UserSettingsSection onNavigate={navigateTo} onClose={t54} />;
    $[148] = navigateTo;
    $[149] = t54;
    $[150] = t55;
  } else {
    t55 = $[150];
  }
  let t56;
  if ($[151] !== navigateTo) {
    t56 = <InternalToolsSection onNavigate={navigateTo} />;
    $[151] = navigateTo;
    $[152] = t56;
  } else {
    t56 = $[152];
  }
  let t57;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t57 = <Divider />;
    $[153] = t57;
  } else {
    t57 = $[153];
  }
  let t58;
  if ($[154] === Symbol.for("react.memo_cache_sentinel")) {
    t58 = <Logout3Linear className="size-5" />;
    $[154] = t58;
  } else {
    t58 = $[154];
  }
  let t59;
  if ($[155] !== t) {
    t59 = t("logout");
    $[155] = t;
    $[156] = t59;
  } else {
    t59 = $[156];
  }
  let t60;
  if ($[157] !== handleLogout || $[158] !== isLoggingOut || $[159] !== t59) {
    t60 = <div className="flex w-full flex-col p-2"><MenuRow icon={t58} label={t59} onPress={handleLogout} isLoading={isLoggingOut} testId="sidebar-logout" trackingLabel="Log Out" /></div>;
    $[157] = handleLogout;
    $[158] = isLoggingOut;
    $[159] = t59;
    $[160] = t60;
  } else {
    t60 = $[160];
  }
  let t61;
  if ($[161] !== t46 || $[162] !== t47 || $[163] !== t52 || $[164] !== t55 || $[165] !== t56 || $[166] !== t60) {
    t61 = <PopoverContent className="w-80 max-w-[calc(100vw-5rem)] p-0"><div className="flex w-full flex-col">{t46}{t47}{t48}{t52}{t53}{t55}{t56}{t57}{t60}</div></PopoverContent>;
    $[161] = t46;
    $[162] = t47;
    $[163] = t52;
    $[164] = t55;
    $[165] = t56;
    $[166] = t60;
    $[167] = t61;
  } else {
    t61 = $[167];
  }
  const menuPopoverContent = t61;
  const popoverContent = navigationUpdatesEnabled ? menuPopoverContent : legacyPopoverContent;
  const t62 = isDesktop ? "right-start" : "top";
  let t63;
  if ($[168] !== isPending || $[169] !== organizations.length || $[170] !== searchValue) {
    t63 = [organizations.length, isPending, searchValue];
    $[168] = isPending;
    $[169] = organizations.length;
    $[170] = searchValue;
    $[171] = t63;
  } else {
    t63 = $[171];
  }
  const t64 = trigger ?? defaultTrigger;
  let t65;
  if ($[172] !== t64) {
    t65 = <PopoverTrigger>{t64}</PopoverTrigger>;
    $[172] = t64;
    $[173] = t65;
  } else {
    t65 = $[173];
  }
  let t66;
  if ($[174] !== handleOpenChange || $[175] !== isOpen || $[176] !== popoverContent || $[177] !== t62 || $[178] !== t63 || $[179] !== t65) {
    t66 = <Popover isOpen={isOpen} onOpenChange={handleOpenChange} placement={t62} offset={8} shouldCloseOnScroll={false} updatePositionDeps={t63}>{t65}{popoverContent}</Popover>;
    $[174] = handleOpenChange;
    $[175] = isOpen;
    $[176] = popoverContent;
    $[177] = t62;
    $[178] = t63;
    $[179] = t65;
    $[180] = t66;
  } else {
    t66 = $[180];
  }
  const popover = t66;
  if (avatarOnly && !trigger) {
    let t67;
    if ($[181] !== popover) {
      t67 = <div className="w-full">{popover}</div>;
      $[181] = popover;
      $[182] = t67;
    } else {
      t67 = $[182];
    }
    let t68;
    if ($[183] !== t67 || $[184] !== userData.displayName) {
      t68 = <Tooltip content={userData.displayName} placement="right" color="foreground">{t67}</Tooltip>;
      $[183] = t67;
      $[184] = userData.displayName;
      $[185] = t68;
    } else {
      t68 = $[185];
    }
    return t68;
  }
  return popover;
};
function _temp() {
  delete document.documentElement.dataset.overlayOpen;
}